/**
 * THE TWO THINGS DONE WITH A BLOB THAT ARE NOT A PREVIEW.
 *
 * `useBlobUrl` next door covers the fetch-and-show lifecycle. These cover the other two,
 * each of which had been written twice:
 *
 *   HAND IT TO SOMETHING that needs the URL past this tick — a new tab, the cover-panel
 *   viewer. Attachment's `withBytes` and ProjectPanel's `onBodyClick` both minted a URL,
 *   passed it on, and revoked on a 30-second timer; ProjectPanel's comment even said
 *   "same 30s grace as the card's withBytes", which is a copy admitting it is one.
 *
 *   SAVE IT TO DISK. NoteAttachments and AgentPanel both built an <a download>, clicked
 *   it, and revoked on a 10-second timer, line for line.
 *
 * WHY A TIMER AT ALL, and why the two differ. Revoking synchronously races the consumer:
 * `el.click()` has only QUEUED a navigation when it returns, and some engines fetch the
 * URL after the current task — revoke first and the download silently produces nothing.
 * So each hands over and then lets go on a delay long enough to be safe and short enough
 * that the bytes are not pinned for the life of the page. A download is handled within a
 * tick or two; a viewer may hold the URL while somebody reads the document, which is why
 * that one is much longer.
 *
 * FileViewer keeps its own mint deliberately: it builds a URL from base64 already in
 * hand, inside an effect, with a synchronous revoke in the teardown — no fetch, no
 * failure, no handover. Folding it in would mean a second mode in one of these for a
 * shape that shares nothing with them but the word "blob".
 */

/** A viewer may hold the URL while someone reads. Long enough to be safe, short enough
 *  not to pin the bytes for the life of the page. */
const HANDOVER_GRACE_MS = 30_000

/** A queued navigation is picked up within a tick or two, never seconds. */
const DOWNLOAD_GRACE_MS = 10_000

/**
 * Give the consumer an object URL for `blob`, then release it after a grace period.
 *
 * The consumer may keep the URL past this tick — that is the whole reason this exists
 * rather than a try/finally.
 */
// The consumer parameter is `consume`, not `use`: a parameter named `use` reads as
// React's use() hook to both the linter and a tired human, and this file is nowhere
// near React.
export function withBlobUrl(
  blob: Blob,
  consume: (url: string) => void,
  graceMs = HANDOVER_GRACE_MS,
): void {
  const url = URL.createObjectURL(blob)
  consume(url)
  setTimeout(() => URL.revokeObjectURL(url), graceMs)
}

/**
 * Save `blob` to the viewer's disk under `filename`.
 *
 * The anchor is never attached to the document: a detached element's `click()` still
 * starts the download in every engine this app runs on, and appending one means
 * remembering to remove it — a second lifecycle to get wrong beside the URL's.
 */
export function downloadBlob(blob: Blob, filename: string): void {
  withBlobUrl(
    blob,
    (url) => {
      const el = document.createElement('a')
      el.href = url
      el.download = filename
      el.click()
    },
    DOWNLOAD_GRACE_MS,
  )
}
